import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, FileText, Info, Loader2, ScanLine } from 'lucide-react';
import { API_BASE, apiGet, apiPost, authedFetch } from '@/lib/api';

/** A question with two buttons. The long explanation hides behind the info dot,
 * so the card stays short enough to read on a phone. */
function Ask({ question, why, onYes, onNo, yesLabel = 'Yes', noLabel = 'No', testId, busy }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3" data-testid={testId}>
      <p className="font-heading text-sm text-slate-900 flex items-start gap-1.5">
        <span className="flex-1">{question}</span>
        {why && (
          <button type="button" onClick={() => setOpen(o => !o)} data-testid={`${testId}-info`}
            aria-label="Why we ask" className="text-slate-500 hover:text-slate-800 flex-shrink-0">
            <Info className="w-3.5 h-3.5" />
          </button>
        )}
      </p>
      {open && why && (
        <p className="mt-1.5 font-body text-[12px] leading-relaxed text-slate-500" data-testid={`${testId}-why`}>
          {why}
        </p>
      )}
      <div className="flex gap-2 mt-2.5">
        <Button size="sm" className="h-8 text-[12px] bg-slate-900 text-white hover:bg-slate-800"
          disabled={busy} onClick={onYes} data-testid={`${testId}-yes`}>
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}{yesLabel}
        </Button>
        <Button size="sm" variant="outline" className="h-8 text-[12px]" disabled={busy}
          onClick={onNo} data-testid={`${testId}-no`}>{noLabel}</Button>
      </div>
    </div>
  );
}

/**
 * Snap & Solve. The child photographs pages; we say what it is, whether we
 * already have it, and offer the two things they can do — in buttons, not
 * paragraphs. Discussion is gated to their own grade.
 */
export function SnapSolveDialog({ open, onOpenChange, file, onSolveAsQuestion, onSwitchTo }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState('reading');      // reading | decided | error
  const [decision, setDecision] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState('upload');         // upload -> discuss

  const read = useCallback(async (f) => {
    setPhase('reading'); setError(''); setDecision(null); setStep('upload');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await authedFetch(`${API_BASE}/syllabus/ai/analyse?purpose=snap`, { method: 'POST', body: fd });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || 'We could not read that file.');
      const verdict = await apiGet(`/snap/decide/${body.analysis_id}`);
      setDecision(verdict);
      setStep(verdict.offer_upload ? 'upload' : 'discuss');
      setPhase('decided');
    } catch (e) {
      setError(e?.message || 'We could not read that file.');
      setPhase('error');
    }
  }, []);

  useEffect(() => { if (open && file) read(file); }, [open, file, read]);

  const startDiscussion = async () => {
    setBusy(true);
    try {
      const res = await apiPost('/snap/discuss', { analysis_id: decision.analysis_id });
      onOpenChange(false);
      navigate(`/home?session=${res.session_id}`);
      toast.success(`Chat open on ${res.topic}`);
    } catch (e) { toast.error(e?.message || 'Could not start that chat'); }
    setBusy(false);
  };

  const toSyllabus = () => {
    onOpenChange(false);
    const d = decision?.detected || {};
    const q = new URLSearchParams({ upload: '1', ...(d.subject ? { subject: d.subject } : {}) });
    navigate(`/syllabus?${q.toString()}`);
  };

  const isQuestionPhoto = decision && (!decision.readable || !decision.detected?.chapters_found);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md rounded-3xl" data-testid="snap-solve-dialog">
        <DialogTitle className="font-heading text-lg font-bold text-slate-900 flex items-center gap-2">
          <ScanLine className="w-4 h-4 text-emerald-500" /> Snap &amp; Solve
        </DialogTitle>
        <DialogDescription className="font-body text-[12px] text-slate-500 flex items-center gap-1.5 break-all">
          <FileText className="w-3.5 h-3.5 flex-shrink-0" />
          {file?.name ? <>You have uploaded <span className="font-heading text-slate-700">{file.name}</span></> : 'No file picked'}
        </DialogDescription>

        {phase === 'reading' && (
          <p className="flex items-center gap-2 py-6 font-body text-sm text-slate-500" data-testid="snap-reading">
            <Loader2 className="w-4 h-4 animate-spin" /> Reading your pages…
          </p>
        )}

        {phase === 'error' && (
          <div className="py-3" data-testid="snap-error">
            <p className="font-body text-sm text-rose-600">{error}</p>
            <Button size="sm" variant="outline" className="h-8 text-[12px] mt-2"
              onClick={() => file && read(file)} data-testid="snap-retry">Try reading it again</Button>
          </div>
        )}

        {phase === 'decided' && decision && (
          <div className="space-y-2.5">
            <p className="font-body text-[13px] text-slate-700 flex items-start gap-1.5" data-testid="snap-headline">
              {decision.match?.found
                ? <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                : <ScanLine className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />}
              {isQuestionPhoto ? 'This looks like a question, not a chapter.' : decision.headline}
            </p>

            {isQuestionPhoto && (
              <Ask testId="snap-ask-solve" question="Shall I solve it with you?"
                why="I will read the photo and work through it step by step in the chat."
                onYes={() => { onOpenChange(false); onSolveAsQuestion?.(file); }}
                onNo={() => onOpenChange(false)} />
            )}

            {!isQuestionPhoto && step === 'upload' && decision.offer_upload && (
              <Ask testId="snap-ask-upload" question="Add this chapter to Gurukul Veda?"
                why="We do not have this chapter yet. Upload it under Syllabus with the book's name and contents page — once an admin approves it, everyone in your class gets the material and you earn Gurukul Veda coins."
                onYes={toSyllabus} onNo={() => setStep('discuss')}
                yesLabel="Yes, take me there" noLabel="Not now" />
            )}

            {!isQuestionPhoto && (step === 'discuss' || !decision.offer_upload) && (
              <>
                {decision.in_your_syllabus && decision.switch_hint && (
                  <Ask testId="snap-ask-switch"
                    question={`Switch to ${decision.switch_hint.subject} · ${decision.switch_hint.chapter}?`}
                    why="This chapter is already in your syllabus. Switching at the top lets me use the approved material — summaries, graphics and quizzes — instead of only your photo."
                    onYes={() => { onOpenChange(false); onSwitchTo?.(decision.switch_hint); }}
                    onNo={() => { /* stay and offer the scan chat below */ }}
                    yesLabel="Yes, switch" noLabel="No, use my scan" />
                )}

                {decision.can_discuss ? (
                  <Ask testId="snap-ask-discuss" question="Discuss this chapter now?" busy={busy}
                    why="The chat opens straight away on these pages — no waiting for an admin. I stay inside what you uploaded."
                    onYes={startDiscussion} onNo={() => onOpenChange(false)}
                    yesLabel="Yes, let's talk" noLabel="Close" />
                ) : (
                  <Ask testId="snap-ask-blocked" question="This is from another grade — upload it instead?"
                    why="We only discuss chapters from your own grade and board, so one subscription stays with one student. Upload it under Syllabus and, once approved, you earn Gurukul Veda coins for it."
                    onYes={toSyllabus} onNo={() => onOpenChange(false)}
                    yesLabel="Open Syllabus" noLabel="Close" />
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default SnapSolveDialog;
